/**
 * DashboardPage — landing page after a successful login.
 *
 * Session 5 build. Replaces the previous placeholder.
 *
 * Layout (top → bottom):
 *
 *   1. Header bar
 *      - App name on the start side
 *      - LanguageToggle + an Account menu (avatar IconButton → menu
 *        with "Edit profile" and "Log out") on the end side
 *
 *   2. Greeting block
 *      - "Hello, {{firstName}}!" + a brief subtitle
 *
 *   3. Profile summary card — the "you are correctly known" card
 *      The user just trusted us with health data, so we reflect it
 *      back so they can verify it's right. We deliberately keep this
 *      compact: name, age + gender, phone, chronic conditions, drug
 *      and food allergies. The full address and edit form live on a
 *      future /profile page reached from the "Edit" button.
 *
 *   4. Quick actions — three nav cards for the main customer areas
 *      (Services / Transactions / Service history). None of those
 *      pages exist yet, so the cards show a "Coming soon" badge and
 *      are non-interactive. Future sessions wire each card to its
 *      destination route.
 *
 * Why we don't show address on the summary:
 *   Address can be up to 500 chars (BACKEND_CONTRACT.md). On a small
 *   card it dominates the layout. The full profile page (future
 *   session) is the right place to display it.
 *
 * What this page assumes about Redux state:
 *   - `user` is non-null. That's enforced by ProtectedRoute, which
 *     redirects authenticated-but-profile-less users to /signup
 *     before they ever reach here (see Session 4 follow-up notes).
 *     We still guard against `user == null` defensively because Redux
 *     could be mid-rehydrate, but we don't have to design for it.
 */

import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  Button,
} from '@mui/material';
import Container from '@/components/PageSheet';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryIcon from '@mui/icons-material/History';
import TokenIcon from '@mui/icons-material/Token';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import DashboardTile from '@/components/DashboardTile';
import DashboardCandleCard from '@/features/candleCard/DashboardCandleCard';
import { TONES } from '@/components/dashboardTones';

import SupportChooserDialog from '@/components/SupportChooserDialog';
import CurrentClinicBanner from '@/components/CurrentClinicBanner';
import { selectCurrentClinic } from '@/store/clinicSlice';
import { useAuth } from '@/hooks/useAuth';
import { notificationsApi, consultationsApi } from '@/services/api';
import { formatPhoneDisplay } from '@/utils/phone';

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Translate a chronic-disease code to its localized label.
 * Falls back to the raw code if it's a user-entered freeSolo value
 * not in the predefined list. Same pattern as SignupPage; duplicated
 * here intentionally (3 lines, not worth extracting yet — see
 * tech debt #7 in CLAUDE.md if this pops up a third time).
 *
 * @param {string} code
 * @param {(key: string) => string} t
 */
function getDiseaseLabel(code, t) {
  const key = `signup.diseases.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}

/**
 * Build a comma-joined display string for a list, or return the
 * "None" translation if empty/missing. The separator is "، " for
 * Persian (Arabic comma) and ", " for English so the punctuation
 * matches the surrounding script.
 *
 * @param {string[] | undefined} arr
 * @param {(key: string) => string} t
 * @param {string} language       'fa' | 'en'
 * @param {(item: string) => string} [translateItem]  optional per-item translator
 */
function listOrNone(arr, t, language, translateItem) {
  if (!arr || arr.length === 0) return t('dashboard.profile.none');
  const sep = language === 'fa' ? '، ' : ', ';
  return arr.map((x) => (translateItem ? translateItem(x) : x)).join(sep);
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

/**
 * Compact card showing the user's stored profile.
 * One row per field with a small label and the value.
 */
function ProfileSummaryCard({ user, t, language, onEdit }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  const genderLabel = user.gender
    ? t(`signup.fields.gender.${user.gender}`)
    : '';

  // Compose age + gender on one line: "35 years old • Male" or "۳۵ ساله • مرد"
  const ageGenderLine =
    user.age && user.gender
      ? t('dashboard.profile.ageGenderLine', {
          age: user.age,
          gender: genderLabel,
        })
      : user.age
        ? t('dashboard.profile.ageOnly', { age: user.age })
        : genderLabel;

  /** Render one labeled row with an optional icon on the start side. */
  const InfoRow = ({ icon, label, value }) => (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ py: 0.5 }}
    >
      {icon && (
        <Box sx={{ color: 'text.secondary', mt: 0.25 }} aria-hidden>
          {icon}
        </Box>
      )}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header: clickable title (toggles) + edit button */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: profileOpen ? 1.5 : 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            onClick={() => setProfileOpen((o) => !o)}
            sx={{ flex: 1, cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {t('dashboard.profile.title')}
            </Typography>
            {profileOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Stack>
          <Button
            size="small"
            variant="text"
            startIcon={<EditIcon fontSize="small" />}
            onClick={onEdit}
          >
            {t('dashboard.profile.edit')}
          </Button>
        </Stack>

        <Collapse in={profileOpen} timeout="auto" unmountOnExit>
        {/* Identity block */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" component="div" fontWeight={600}>
            {fullName || '—'}
          </Typography>
          {ageGenderLine && (
            <Typography variant="body2" color="text.secondary">
              {ageGenderLine}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Contact + health rows */}
        <Stack spacing={0.5}>
          {user.phone && (
            <InfoRow
              icon={<PhoneIphoneIcon fontSize="small" />}
              label={t('auth.phoneLabel')}
              value={
                <Box component="span" dir="ltr">
                  {formatPhoneDisplay(user.phone)}
                </Box>
              }
            />
          )}
          <InfoRow
            icon={<HealthAndSafetyIcon fontSize="small" />}
            label={t('signup.fields.chronicDiseases.label')}
            value={listOrNone(user.chronicDiseases, t, language, (c) =>
              getDiseaseLabel(c, t)
            )}
          />
          <InfoRow
            icon={<MedicationIcon fontSize="small" />}
            label={t('signup.fields.drugAllergies.label')}
            value={listOrNone(user.drugAllergies, t, language)}
          />
          <InfoRow
            icon={<RestaurantIcon fontSize="small" />}
            label={t('signup.fields.foodAllergies.label')}
            value={listOrNone(user.foodAllergies, t, language)}
          />
        </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

/**
 * Banner showing which clinic the user is currently "in".
 *
 * Extracted to `@/components/CurrentClinicBanner` in Session 18.1 so the
 * Services page can render it too. Imported above.
 */

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const language = i18n.language?.startsWith('fa') ? 'fa' : 'en';

  // The "capture my face" action only exists where the entered clinic's policy
  // allows clients to capture their own photos.
  const currentClinic = useSelector(selectCurrentClinic);
  const canSelfCapture = currentClinic?.capturePolicy === 'client_capture';

  // Single "Support" entry → choose app vs clinic in a popup.
  const [supportOpen, setSupportOpen] = useState(false);

  // Unread ticket badges. refetchOnMount:'always' (global) keeps this fresh on
  // every return to the dashboard, so reading a ticket clears the badge.
  const { data: unreadResp } = useQuery({
    queryKey: ['unread-summary'],
    queryFn: () => notificationsApi.getUnreadSummary(),
  });
  const unreadTotal = unreadResp?.data?.total ?? 0;

  // Consultations needing the client's attention: unread physician messages,
  // plus reports the physician has just confirmed.
  const { data: consultAlerts } = useQuery({
    queryKey: ['consultation-alerts'],
    queryFn: () => consultationsApi.listConsultations().then((list) =>
      list.reduce((n, c) => n + (c.unreadCount || 0), 0)),
    refetchInterval: 120000,
  });

  /**
   * Logout: tell the backend to invalidate the refresh cookie, clear
   * local auth state regardless of the request's outcome (offline /
   * network failure shouldn't trap us logged in), then bounce to /login.
   */

  /** Send the user to /profile. Single callback used by both the
   *  account-menu "Edit profile" item and the profile card's Edit button. */
  const handleEditProfile = () => {
    navigate('/profile');
  };

  // Defensive fallback. In practice ProtectedRoute keeps us off this
  // page when user is null, but we guard anyway so a transient redux
  // state doesn't blow up the render.
  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography>{t('common.loading')}</Typography>
      </Container>
    );
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const displayName = fullName || t('dashboard.welcomeAnonymous');
  const monogram = user.firstName?.charAt(0)?.toUpperCase() || '?';
  const hour = new Date().getHours();
  const partOfDay = hour < 5 ? 'night' : hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 21 ? 'evening' : 'night';
  const timeGreeting = t(`dashboard.greeting.${partOfDay}`);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>

        {/* Greeting welcome card — matches the clinic app's compact header. */}
        <Box sx={{
          position: 'relative', overflow: 'hidden',
          bgcolor: 'background.paper', borderRadius: '20px',
          boxShadow: '0 6px 20px -16px rgba(17, 34, 62, 0.55)',
          py: 1.5, px: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box aria-hidden sx={{
            position: 'absolute', insetInlineStart: 0, top: 12, bottom: 12, width: 4,
            borderRadius: '0 4px 4px 0', background: 'linear-gradient(#8C6D1F, #B7994A)',
          }} />
          <Box sx={{
            width: 48, height: 48, borderRadius: 3, flexShrink: 0,
            display: 'grid', placeItems: 'center', color: '#fff',
            fontSize: 20, fontWeight: 800, lineHeight: 1,
            background: 'linear-gradient(140deg, #11223E 0%, #2C4A78 100%)',
            boxShadow: '0 8px 18px -10px rgba(17,34,62,0.8)',
            border: '2px solid rgba(140,109,31,0.55)',
          }}>
            {monogram}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: 'secondary.main' }} noWrap>
              {timeGreeting}
            </Typography>
            <Typography component="h1" sx={{ fontSize: 21, fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.01em' }} noWrap>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {t('dashboard.welcomeSubtitle')}
            </Typography>
          </Box>
        </Box>

        {/* The client's own card, directly under the greeting: the first thing
            on the dashboard is the thing that is theirs. It is the real card,
            not a tile standing in for one, so opening the section feels like
            turning over something you were already holding. */}
        <DashboardCandleCard />

        {/* Current-clinic banner — "you are currently at {clinic}" + change */}
        <CurrentClinicBanner />

        <ProfileSummaryCard
          user={user}
          t={t}
          language={language}
          onEdit={handleEditProfile}
        />

        {/* Quick actions section */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 1.5 }}
            component="h2"
          >
            {t('dashboard.actions.title')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
            }}
          >
            <DashboardTile tone={TONES.primary} icon={<MedicalInformationOutlinedIcon />} title={t('dashboard.nav.consultations.title')} desc={t('dashboard.nav.consultations.description')} onClick={() => navigate('/consultations')} badgeCount={consultAlerts || 0} />
            <DashboardTile tone={TONES.primary} icon={<StickyNote2OutlinedIcon />} title={t('dashboard.nav.notes.title')} desc={t('dashboard.nav.notes.description')} onClick={() => navigate('/notes')} />
            <DashboardTile tone={TONES.brass} icon={<MedicalServicesIcon />} title={t('dashboard.nav.services.title')} desc={t('dashboard.nav.services.description')} onClick={() => navigate('/services')} />
            <DashboardTile tone={TONES.stone} icon={<ReceiptLongIcon />} title={t('dashboard.nav.transactions.title')} desc={t('dashboard.nav.transactions.description')} onClick={() => navigate('/transactions')} />
            <DashboardTile tone={TONES.primary} icon={<HistoryIcon />} title={t('dashboard.nav.history.title')} desc={t('dashboard.nav.history.description')} onClick={() => navigate('/service-history')} />
            {canSelfCapture && (
  <DashboardTile tone={TONES.primary} icon={<FaceRetouchingNaturalIcon />} title={t('dashboard.nav.faceCapture.title')} desc={t('dashboard.nav.faceCapture.description')} onClick={() => navigate('/face-capture')} />
            )}
            <DashboardTile tone={TONES.brass} icon={<FolderSharedIcon />} title={t('dashboard.nav.sharing.title')} desc={t('dashboard.nav.sharing.description')} onClick={() => navigate('/sharing')} />
            <DashboardTile tone={TONES.brass} icon={<TokenIcon />} title={t('dashboard.nav.credits.title')} desc={t('dashboard.nav.credits.description')} onClick={() => navigate('/credits')} />
            <DashboardTile tone={TONES.stone} icon={<ContactSupportIcon />} title={t('dashboard.nav.support.title')} desc={t('dashboard.nav.support.description')} onClick={() => setSupportOpen(true)} badgeCount={unreadTotal} />
          </Box>
        </Box>
      </Stack>

      {/* Support channel picker — app vs clinic */}
      <SupportChooserDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        onChoose={(domain) => {
          setSupportOpen(false);
          navigate(domain === 'clinic' ? '/clinic-tickets' : '/support');
        }}
      />
    </Container>
  );
}

export default DashboardPage;
